import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import CountrySelect from 'components/CountrySelect';
import {
  selectSearchRateLimitConfig,
  selectSearchRateLimitError,
  selectSearchRateLimitLoading,
} from 'domains/searchRateLimit/SearchRateLimit.selectors';
import {
  fetchSearchRateLimitConfigRequest,
  resetSearchRateLimitState,
} from 'domains/searchRateLimit/SearchRateLimit.store';
import { SectionWrapper } from 'pages/SettingsPage/General/GeneralSettings.styled';
import { Flex, MarginBox, Spin, Text } from 'UI';
import { Country } from 'utils/i18n/Country';
import { CountryRateLimitForm } from './components/CountryRateLimitForm';
import { GarageOverridesList } from './components/GarageOverridesList';
import { RateLimitMetrics } from './components/RateLimitMetrics';

interface SearchRateLimitSettingsProps {
  countryCode?: string;
}

export const SearchRateLimitSettings: React.FC<SearchRateLimitSettingsProps> = ({ countryCode = 'FR' }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedCountry, setSelectedCountry] = useState<Country>(Country.findByKeyMandatory(countryCode));

  const config = useSelector(selectSearchRateLimitConfig);
  const loading = useSelector(selectSearchRateLimitLoading);
  const error = useSelector(selectSearchRateLimitError);

  useEffect(() => {
    dispatch(fetchSearchRateLimitConfigRequest(selectedCountry.key));
  }, [selectedCountry, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetSearchRateLimitState());
    };
  }, [dispatch]);

  const handleSelectCountry = (country: Country) => {
    setSelectedCountry(country);
  };

  return (
    <MarginBox mx={24} my={24}>
      <Flex direction={'column'}>
        {/* Page header */}
        <MarginBox mb={8}>
          <Text type="heading3">{t('backoffice.search-rate-limit.title', 'Search Rate Limit Configuration')}</Text>
        </MarginBox>
        <MarginBox mb={24}>
          <Text type="base">
            {t('backoffice.search-rate-limit.subtitle', 'Manage VIN/VRN search rate limits per country and garage')}
          </Text>
        </MarginBox>

        {/* Country selection */}
        <SectionWrapper data-cy={'search-rate-limit-country-selection'} $marginY={15}>
          <Flex direction={'column'} gap={15}>
            <Text type="heading4">{t('common.select_country', 'Select country')}</Text>
            <Text type="base">
              {t(
                'backoffice.search-rate-limit.country.description',
                'Select a country to view and edit its VIN/VRN search rate limit configuration.',
              )}
            </Text>
            <CountrySelect
              countryList={Country.listCountries()}
              selected={selectedCountry}
              onSelect={handleSelectCountry}
            />
          </Flex>
        </SectionWrapper>

        {/* Loading */}
        {loading && !config && (
          <Flex justify={'center'}>
            <MarginBox my={40}>
              <Spin size={'large'} />
            </MarginBox>
          </Flex>
        )}

        {/* Error */}
        {error && (
          <SectionWrapper data-cy={'search-rate-limit-error'} $marginY={15}>
            <Text type={'error'}>{error}</Text>
          </SectionWrapper>
        )}

        {/* Global limits form */}
        <SectionWrapper data-cy={'search-rate-limit-global-config'} $marginY={15}>
          <Flex direction={'column'} gap={15}>
            <Text type="heading4">{t('backoffice.search-rate-limit.global-limits.title', 'Global Limits')}</Text>
            <Text type="base">
              {t(
                'backoffice.search-rate-limit.global-limits.description',
                'Configure the default short-term and long-term search limits applied to all garages in this country.',
              )}
            </Text>
            <CountryRateLimitForm countryCode={selectedCountry.key} config={config} />
          </Flex>
        </SectionWrapper>

        {/* Garage overrides */}
        <SectionWrapper data-cy={'search-rate-limit-garage-overrides'} $marginY={15}>
          <Flex direction={'column'} gap={15}>
            <Text type="heading4">{t('backoffice.search-rate-limit.garage-overrides.title', 'Garage Overrides')}</Text>
            <Text type="base">
              {t(
                'backoffice.search-rate-limit.garage-overrides.description',
                'Add per-garage rate limit overrides that take precedence over the global limits.',
              )}
            </Text>
            <GarageOverridesList countryCode={selectedCountry.key} config={config} />
          </Flex>
        </SectionWrapper>

        {/* Violations metrics */}
        <SectionWrapper data-cy={'search-rate-limit-violations'} $marginY={15}>
          <Flex direction={'column'} gap={15}>
            <Text type="heading4">{t('backoffice.search-rate-limit.violations.title', 'Recent Violations')}</Text>
            <Text type="base">
              {t(
                'backoffice.search-rate-limit.violations.description',
                'Overview of recent VIN/VRN search rate limit violations for this country.',
              )}
            </Text>
            <RateLimitMetrics countryCode={selectedCountry.key} />
          </Flex>
        </SectionWrapper>
      </Flex>
    </MarginBox>
  );
};

export default SearchRateLimitSettings;
